import { getSignedCookies } from '@api/signedCookies'
import { parseAppHost } from '@utils/AppHostUtil'

/**
 * AssetCloudFrontCookieService manages CloudFront signed cookies for accessing assets.
 *
 * Instead of generating signed URLs per asset, this service:
 * 1. Fetches signed cookies once from the Gateway API
 * 2. Sets them in the browser with path=/assets: host-only on a regional app.justwalkout.com host,
 *    domain=.storemanagement.jihm.amazon.dev on the legacy hosts
 * 3. Browser automatically sends these cookies with all /assets/* requests
 * 4. Refreshes cookies every hour if they're within 2 hours of expiration
 *
 * This enables blanket access to all assets under /assets/* without per-object URL signing.
 */
export class AssetCloudFrontCookieService {
    private expiresAtMillis: number | undefined
    private timerId: ReturnType<typeof setTimeout> | undefined

    /**
     * Initializes the service by fetching and setting cookies, then starting the heartbeat.
     * Safe to call multiple times — skips if cookies are still valid.
     */
    public async init () {
        // Skip if cookies are already set and not near expiration (2 hours)
        if (this.expiresAtMillis && (this.expiresAtMillis - Date.now()) > 2 * 60 * 60 * 1000) {
            return
        }
        if (this.timerId) {
            clearTimeout(this.timerId)
        }
        await this.refreshCookies()
        this.scheduleHeartbeat()
    }

    /**
     * Fetches signed cookies from Gateway API and sets them in the browser.
     * The Gateway returns three cookie header values:
     * - CloudFront-Policy: Base64-encoded policy with expiration and resource path
     * - CloudFront-Signature: Signature of the policy
     * - CloudFront-Key-Pair-Id: Public key ID used for signing
     */
    private async refreshCookies () {
        try {
            const hostname = window.location.hostname
            const appHost = parseAppHost(hostname)

            // On an app host, assets are served from the page's own origin/cell, so sign the cookies
            // with that host's own realm rather than the active cell (which follows the selected
            // store/region and can differ). On legacy hosts no realm is passed, so the active-cell
            // client is used exactly as before.
            const response = await getSignedCookies(appHost?.realm)

            // Set cookies with domain and path scope
            // App host: host-only, so one region's tab can't overwrite another's (no refetch on 403).
            // Legacy host: last 4 labels, e.g. .storemanagement.jihm.amazon.dev.
            // Path: /assets (only sent for requests to /assets/*)
            const domainAttr = appHost ? '' : `; domain=.${hostname.split('.').slice(-4).join('.')}`
            const path = '/assets'

            // Set the three CloudFront cookies - they already include the cookie name from Gateway
            document.cookie = `${response.cloudFrontPolicy}${domainAttr}; path=${path}; secure`
            document.cookie = `${response.cloudFrontSignature}${domainAttr}; path=${path}; secure`
            document.cookie = `${response.cloudFrontKeyPairId}${domainAttr}; path=${path}; secure`

            this.expiresAtMillis = response.expirationDate.getTime()
            console.log('AssetCloudFrontCookieService: Cookies set, expires at', response.expirationDate)
        } catch (error) {
            console.error('AssetCloudFrontCookieService: Failed to refresh cookies', error)
        }
    }

    /**
     * Schedules periodic checks (every hour) to refresh cookies if they're within 2 hours of expiration.
     * With 1-day expiration, this will refresh at the 22-hour mark.
     */
    private scheduleHeartbeat () {
        this.timerId = setTimeout(() => {
            if (this.expiresAtMillis) {
                const remaining = this.expiresAtMillis - Date.now()
                // Refresh if less than 2 hours remaining
                if (remaining <= 2 * 60 * 60 * 1000) {
                    this.refreshCookies()
                }
            } else {
                // Retry if initial fetch failed
                this.refreshCookies()
            }
            this.scheduleHeartbeat()
        }, 60 * 60 * 1000) // Check every hour
    }
}

export default new AssetCloudFrontCookieService()
